/**
 * FAQ et métadonnées chiffrées d'un événement à Cannes, en trois langues.
 *
 * Même recette que le Festival de Cannes (01/10/2026) : la première question
 * d'un client est le prix, et les pages événement n'y répondaient pas. Les
 * montants viennent d'EVENT_PRICES (lu dans infos.ts) ; ce module ne porte
 * que la formulation, pour que MIPIM, Cannes Lions ou le Yachting Festival
 * disent la même chose sans recopier le texte.
 */
import { EVENT_PRICES as P } from "./eventPricing";

type Faq = { q: string; a: string };

/** `during` : « pendant le MIPIM » / « during MIPIM » / « خلال MIPIM ». */
export const cannesEventPriceFaq = (
  locale: "fr" | "en" | "ar",
  during: string,
): Faq[] => {
  if (locale === "en")
    return [
      {
        q: `How much does a private chauffeur cost ${during}?`,
        a: `Hourly hire starts at €${P.e3} for 3 hours in a Class E, €${P.v3} in a Class V (7 seats) and €${P.s3} in a Class S. A full 8-hour day costs €${P.e8}, €${P.v8} or €${P.s8} depending on the class, and each hour beyond the package is billed €${P.extraMin} to €${P.extraMax}. The Nice airport ⇄ Cannes transfer is a fixed price: €${P.niceE}, €${P.niceV} or €${P.niceS}. These are our public rates, and the final price is confirmed in writing before the service.`,
      },
      {
        q: "Transfer or hourly hire: which one should I choose?",
        a: `A transfer is enough to arrive and leave: one journey, a price fixed in advance, from €${P.niceE} from Nice airport. To chain meetings, lunches and evenings, hourly hire keeps the same chauffeur and car, waiting for you between appointments, so you no longer book each journey. It starts at 3 hours.`,
      },
    ];
  if (locale === "ar")
    return [
      {
        q: `كم تكلفة سائق خاص ${during}؟`,
        a: `تبدأ خدمة السائق بالساعة من ${P.e3} يورو لثلاث ساعات في الفئة E، و${P.v3} يورو في الفئة V (7 مقاعد)، و${P.s3} يورو في الفئة S. ويكلّف يوم كامل من 8 ساعات ${P.e8} أو ${P.v8} أو ${P.s8} يورو حسب الفئة، وتُحتسب كل ساعة إضافية بعد الباقة بين ${P.extraMin} و${P.extraMax} يورو. أما النقل بين مطار نيس وكان فبسعر ثابت: ${P.niceE} أو ${P.niceV} أو ${P.niceS} يورو. هذه أسعارنا المعلنة، ويُؤكَّد السعر النهائي كتابيًا قبل الخدمة.`,
      },
      {
        q: "نقل أم سائق بالساعة: ماذا أختار؟",
        a: `يكفي النقل للوصول والمغادرة: رحلة واحدة بسعر محدّد مسبقًا، ابتداءً من ${P.niceE} يورو من مطار نيس. أما للتنقل بين المواعيد والغداء والسهرات، فالخدمة بالساعة تُبقي السائق نفسه والسيارة نفسها في انتظاركم بين موعد وآخر، فلا تحجزون كل رحلة على حدة. وتبدأ من 3 ساعات.`,
      },
    ];
  return [
    {
      q: `Combien coûte un chauffeur privé ${during} ?`,
      a: `Une mise à disposition démarre à ${P.e3} € les 3 h en Classe E, ${P.v3} € en Classe V (7 places) et ${P.s3} € en Classe S. Une journée de 8 h coûte ${P.e8} €, ${P.v8} € ou ${P.s8} € selon la classe, et chaque heure au-delà du forfait est facturée de ${P.extraMin} à ${P.extraMax} €. Le transfert aéroport de Nice ⇄ Cannes est à forfait : ${P.niceE} €, ${P.niceV} € ou ${P.niceS} €. Ce sont nos tarifs publics, et le prix final est confirmé par écrit avant la prestation.`,
    },
    {
      q: "Transfert ou mise à disposition : que choisir ?",
      a: `Le transfert suffit pour arriver et repartir : un trajet, un prix fixé à l'avance, dès ${P.niceE} € depuis l'aéroport de Nice. Pour enchaîner rendez-vous, déjeuners et soirées, la mise à disposition garde le même chauffeur et le même véhicule, qui vous attendent entre deux rendez-vous : vous ne réservez plus chaque trajet. Elle se prend à partir de 3 h.`,
    },
  ];
};

/** Titre et description chiffrés (FR et EN ; l'arabe garde les siens). */
export const cannesEventSeo = (
  locale: "fr" | "en",
  event: string,
  tail: string,
): { title: string; description: string } =>
  locale === "en"
    ? {
        title: `${event} chauffeur from €${P.e3} for 3 hours | KS Rent Services`,
        description: `${event}: hourly chauffeur from €${P.e3} for 3 hours, 8-hour day from €${P.e8}, Nice airport transfer from €${P.niceE}. ${tail}`,
      }
    : {
        title: `Chauffeur privé ${event}, dès ${P.e3} € les 3 h | KS Rent Services`,
        description: `${event} : chauffeur à disposition dès ${P.e3} € les 3 h, journée de 8 h dès ${P.e8} €, transfert aéroport de Nice dès ${P.niceE} €. ${tail}`,
      };
